import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useModalsContext } from "@/contexts/ModalsContext";
import Modal from "@/components/Modals/Modal";
import SignIn from "@/components/Forms/SignIn";
import Register from "@/components/Forms/Register";

// Assets
import AppWhoopLogo from "@/assets/AppWhoop_logo.webp";
import BackgroundImg from "@/assets/background.webp";

import FerxxoImg from "@/assets/eventos/Ferxxo.webp";
import ViejasDeMierdaImg from "@/assets/eventos/ViejasDeMierda.webp";
import ClubAmandaImg from "@/assets/eventos/ClubAmandaDJCeleArrabal.webp";
import JorisVoornImg from "@/assets/eventos/JorisVoorn.webp";
import CelebrarEnAlonsoImg from "@/assets/eventos/CelebrarEnAlonso.webp";

import Mujer1 from "@/assets/recentAmbassador/mujer1.webp";
import Mujer2 from "@/assets/recentAmbassador/mujer2.webp";
import Mujer3 from "@/assets/recentAmbassador/mujer3.webp";
import Mujer4 from "@/assets/recentAmbassador/mujer4.webp";
import Mujer5 from "@/assets/recentAmbassador/mujer5.webp";
import ChrisImg from "@/assets/recentAmbassador/chris.webp";

// ─── Module-level constants ───────────────────────────────────────────────────

const TABS = [
  { id: "empresa", label: "Soy Empresa", icon: "fa-solid fa-building" },
  { id: "asistente", label: "Soy Asistente", icon: "fa-solid fa-ticket" },
  { id: "embajador", label: "Quiero ser Embajador", icon: "fa-solid fa-star" },
];

const EMPRESA_BENEFITS = [
  {
    icon: "fa-solid fa-rocket",
    title: "Gratis para empezar",
    desc: "Crea todos los eventos que necesites. Solo pagas cuando vendes.",
  },
  {
    icon: "fa-solid fa-layer-group",
    title: "Tipos de ticket flexibles",
    desc: "VIP, General, Preventa, Palco — los que necesites, con precios distintos.",
  },
  {
    icon: "fa-solid fa-chart-bar",
    title: "Dashboard en tiempo real",
    desc: "Ventas, recaudación, lista de compradores y rendimiento por embajador.",
  },
  {
    icon: "fa-solid fa-users",
    title: "Red de Embajadores",
    desc: "Invita promotores que venden por ti. Define su comisión entre 3% y 20%.",
  },
  {
    icon: "fa-solid fa-qrcode",
    title: "Control de acceso QR",
    desc: "Múltiples puertas, staff validador y panel en tiempo real.",
  },
  {
    icon: "fa-solid fa-money-bill-wave",
    title: "Corte periódico de ganancias",
    desc: "AppWhoop distribuye los fondos tras cada corte, según términos y condiciones.",
  },
];

const ASISTENTE_BENEFITS = [
  {
    icon: "fa-solid fa-bolt",
    title: "Compra en segundos",
    desc: "Selecciona tu ticket, paga y recibe tu QR al instante.",
  },
  {
    icon: "fa-solid fa-mobile-screen",
    title: "Ticket en tu bolsillo",
    desc: "Accede desde cualquier dispositivo. Sin imprimir nada.",
  },
  {
    icon: "fa-solid fa-shield-halved",
    title: "Pago seguro",
    desc: "Procesado por Klap con encriptación de extremo a extremo.",
  },
  {
    icon: "fa-solid fa-door-open",
    title: "Entrada agilizada",
    desc: "QR validado en segundos en la puerta del evento.",
  },
  // {
  //   icon: "fa-solid fa-heart",
  //   title: "Guarda tus favoritos",
  //   desc: "Sigue a tus organizadores y no te pierdas sus próximos eventos.",
  // },
  {
    icon: "fa-solid fa-share-nodes",
    title: "Comparte con amigos",
    desc: "Invita a tus amigos a las mejores experiencias.",
  },
];

const EMBAJADOR_STEPS = [
  {
    num: "01",
    title: "Activa tu cuenta",
    desc: "Completa tu perfil de embajador con tus datos y cuenta bancaria.",
  },
  {
    num: "02",
    title: "Comparte tu enlace",
    desc: "Cada evento tiene un enlace único tuyo. Compártelo donde quieras.",
  },
  {
    num: "03",
    title: "Cobra tu comisión",
    desc: "Por cada ticket vendido con tu enlace, AppWhoop te paga tu comisión en el siguiente corte de ganancias.",
  },
];

const DIFFERENTIATORS = [
  {
    feature: "Sin costo fijo mensual",
    aw: true,
    other: false,
  },
  {
    feature: "Comisión solo al vender",
    aw: true,
    other: false,
  },
  {
    feature: "Red de embajadores integrada",
    aw: true,
    other: false,
  },
  {
    feature: "Dashboard de embajadores en tiempo real",
    aw: true,
    other: false,
  },
  {
    feature: "Control de acceso QR multi-puerta",
    aw: true,
    other: "extra",
  },
  {
    feature: "Corte periódico de ganancias",
    aw: true,
    other: false,
  },
  {
    feature: "Comisiones configurables por evento",
    aw: true,
    other: false,
  },
  {
    feature: "Soporte en español + equipo local",
    aw: true,
    other: false,
  },
];

const FAQS = [
  {
    q: "¿Cuánto cobra AppWhoop por ticket vendido?",
    a: "Hasta un 10% por ticket, ya incluido en el precio que el comprador paga. El organizador no paga nada extra — AppWhoop cobra solo cuando hay ventas.",
  },
  {
    q: "¿Cómo funciona el programa de embajadores?",
    a: "Si el organizador del evento configura el emplear embajadores para su evento, este define una comisión de entre el 3% y el 20% para los embajadores. Y dependiendo la modalidad de emplear embajadores, cada embajador genera o recibe un enlace único para dicho evento y cobra por cada ticket vendido a través de ese enlace. AppWhoop liquida bajo un corte periódico.",
  },
  {
    q: "¿En qué países está disponible AppWhoop?",
    a: "Actualmente operamos en Chile. La ticketera y la compra de entradas están disponibles para eventos en todo el país. El programa de embajadores opera bajo la jurisdicción legal de AppWhoop SpA, también en Chile. La expansión a LATAM está en camino.",
  },
  {
    q: "¿Cómo recibo el dinero de mis ventas como organizador?",
    a: "Al cerrar el evento, AppWhoop tendrá un proceso de corte periódico que está estipulado en los términos y condiciones. Los fondos se depositan en la cuenta bancaria registrada en tu perfil de empresa, descontadas las comisiones de AppWhoop y embajadores(cuando estas apliquen).",
  },
];

const EVENT_CARDS = [
  { img: FerxxoImg, title: "Ferxxo", category: "Urban / Reggaeton" },
  { img: ViejasDeMierdaImg, title: "Viejas de Mierda", category: "Comedia" },
  { img: ClubAmandaImg, title: "Club Amanda · DJ Cele Arrabal", category: "Electrónica" },
  { img: JorisVoornImg, title: "Joris Voorn", category: "Techno" },
  { img: CelebrarEnAlonsoImg, title: "Celebrar en Alonso", category: "Social" },
];

const AMBASSADOR_PHOTOS_FIXED = [Mujer1, Mujer2, ChrisImg, Mujer3, Mujer4, Mujer5];

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const tabVariants = {
  enter: (dir) => ({ x: dir > 0 ? 50 : -50, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: "easeOut" } },
  exit: (dir) => ({
    x: dir > 0 ? -50 : 50,
    opacity: 0,
    transition: { duration: 0.25, ease: "easeIn" },
  }),
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function BenefitCard({ icon, title, desc, accentClass }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -4 }}
      className="bg-aw-blueblack border border-white/5 rounded-2xl p-5 flex flex-col gap-3 cursor-default"
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentClass}`}
      >
        <FontAwesomeIcon icon={icon} className="text-lg" />
      </div>
      <p className="font-semibold text-white text-sm">{title}</p>
      <p className="text-white/60 text-xs leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function EmpresaTab({ openModal, isAuthenticated }) {
  return (
    <div className="space-y-10">
      {/* Headline */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-center space-y-3"
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Tu evento, tu tiketera,{" "}
          <span className="text-purple-400">tu control total.</span>
        </h2>
        <p className="text-white/60 max-w-xl mx-auto text-sm sm:text-base">
          Crea eventos gratis. Solo pagas cuando vendes — hasta un 10% por
          ticket, ya incluido en el precio.
        </p>
      </motion.div>

      {/* Benefit cards grid */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {EMPRESA_BENEFITS.map((b) => (
          <BenefitCard
            key={b.title}
            icon={b.icon}
            title={b.title}
            desc={b.desc}
            accentClass="bg-purple-400/10 text-purple-400"
          />
        ))}
      </motion.div>

      {/* 3-step process */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="bg-aw-blueblack border border-white/5 rounded-2xl p-6"
      >
        <p className="text-white/50 text-xs uppercase tracking-widest mb-5 font-semibold">
          Cómo empezar
        </p>
        <div className="flex flex-col sm:flex-row gap-6">
          {[
            { step: "1", label: "Crea tu empresa" },
            { step: "2", label: "Publica tu evento" },
            { step: "3", label: "Vende y cobra" },
          ].map(({ step, label }, i) => (
            <div key={step} className="flex items-center gap-3 flex-1">
              <div className="w-9 h-9 rounded-full bg-purple-400/10 border border-purple-400/30 flex items-center justify-center shrink-0">
                <span className="text-purple-400 font-bold text-sm">{step}</span>
              </div>
              <span className="text-white font-medium text-sm">{label}</span>
              {i < 2 && (
                <FontAwesomeIcon
                  icon="fa-solid fa-arrow-right"
                  className="text-white/20 hidden sm:block ml-auto"
                />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="flex justify-center"
      >
        {isAuthenticated ? (
          <Link
            to="/empresa/crear"
            className="bg-purple-400 text-aw-dark font-bold px-8 py-3 rounded-xl hover:scale-95 transition-transform duration-200 text-sm"
          >
            Crear mi empresa →
          </Link>
        ) : (
          <button
            onClick={() => openModal("register")}
            className="bg-purple-400 text-aw-dark font-bold px-8 py-3 rounded-xl hover:scale-95 transition-transform duration-200 text-sm"
          >
            Publicar mi primer evento →
          </button>
        )}
      </motion.div>
    </div>
  );
}

function AsistenteTab() {
  return (
    <div className="space-y-10">
      {/* Headline */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-center space-y-3"
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Descubre experiencias.{" "}
          <span className="text-sky-400">Compra fácil.</span> Ingreso con ticket digital.
        </h2>
   
        <p className="text-white/60 max-w-xl mx-auto text-sm sm:text-base">
          Miles de eventos en Chile, con tickets disponibles al instante desde
          tu celular.
        </p>
      </motion.div>

      {/* Horizontal scrollable event cards */}
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4">
        {EVENT_CARDS.map((ev) => (
          <div
            key={ev.title}
            className="flex-none w-52 sm:w-60 snap-start rounded-2xl overflow-hidden relative border border-white/10"
          >
            <img
              src={ev.img}
              alt={ev.title}
              className="w-full h-72 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 space-y-1">
              <span className="text-xs bg-sky-400/20 text-sky-400 border border-sky-400/30 rounded-full px-2 py-0.5">
                {ev.category}
              </span>
              <p className="text-white font-semibold text-sm leading-tight">
                {ev.title}
              </p>
            </div>
          </div>
        ))}
        {/* Placeholder card */}
        <div className="flex-none w-52 sm:w-60 snap-start rounded-2xl overflow-hidden border border-white/10 bg-aw-blueblack flex flex-col items-center justify-center gap-3 h-72">
          <FontAwesomeIcon
            icon="fa-solid fa-ellipsis"
            className="text-3xl text-white/30"
          />
          <p className="text-white/40 text-sm font-medium text-center px-4">
            Y muchos más
          </p>
        </div>
      </div>

      {/* Benefit cards */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {ASISTENTE_BENEFITS.map((b) => (
          <BenefitCard
            key={b.title}
            icon={b.icon}
            title={b.title}
            desc={b.desc}
            accentClass="bg-sky-400/10 text-sky-400"
          />
        ))}
      </motion.div>

      {/* CTA */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="flex justify-center"
      >
        <Link
          to="/"
          className="bg-sky-400 text-aw-dark font-bold px-8 py-3 rounded-xl hover:scale-95 transition-transform duration-200 text-sm"
        >
          Explorar eventos →
        </Link>
      </motion.div>
    </div>
  );
}

function EmbajadorTab({ openModal, isAuthenticated, userSub }) {
  return (
    <div className="space-y-10">
      {/* Headline */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-center space-y-3"
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Gana dinero recomendando{" "}
          <span className="text-aw-green">lo que ya te gusta.</span>
        </h2>
        <p className="text-white/60 max-w-xl mx-auto text-sm sm:text-base">
          Sin inventario. Sin inversión. Solo comparte tu enlace y recibe
          comisiones por cada ticket vendido.
        </p>
      </motion.div>

      {/* Ambassador photos */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="flex items-center justify-center"
      >
        <div className="flex items-center">
          {AMBASSADOR_PHOTOS_FIXED.map((photo, i) => (
            <img
              key={i}
              src={photo}
              alt={`Embajador ${i + 1}`}
              className={`rounded-full object-cover border-2 border-aw-bluedark -ml-3 first:ml-0 ${
                i === 2 ? "w-16 h-16 z-10 relative" : "w-12 h-12"
              }`}
              style={{ zIndex: i === 2 ? 10 : i }}
            />
          ))}
        </div>
      </motion.div>

      {/* Commission callout */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="bg-aw-green/10 border border-aw-green/30 rounded-2xl p-8 text-center space-y-2"
      >
        <p className="text-aw-green font-black text-5xl sm:text-6xl tracking-tight">
          3% — 20%
        </p>
        <p className="text-white/60 text-sm">
          Definida por el organizador de cada evento
        </p>
      </motion.div>

      {/* 3 steps with connecting line */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="relative"
      >
        {/* Connecting line — desktop only */}
        <div className="hidden sm:block absolute top-7 left-[calc(16.666%+1.5rem)] right-[calc(16.666%+1.5rem)] h-0.5 bg-aw-green/20" />
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-4 relative z-10">
          {EMBAJADOR_STEPS.map((s) => (
            <motion.div
              key={s.num}
              variants={fadeUp}
              className="flex-1 flex flex-col items-center text-center gap-3"
            >
              <div className="w-14 h-14 rounded-full bg-aw-green/10 border-2 border-aw-green/30 flex items-center justify-center shrink-0 bg-aw-bgbluedark">
                <span className="text-aw-green font-black text-lg">{s.num}</span>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{s.title}</p>
                <p className="text-white/50 text-xs mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Chile-only notice */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="bg-amber-400/5 border border-amber-400/30 rounded-2xl p-5 flex gap-3"
      >
        <FontAwesomeIcon
          icon="fa-solid fa-triangle-exclamation"
          className="text-amber-400 text-lg shrink-0 mt-0.5"
        />
        <p className="text-amber-200/80 text-xs leading-relaxed">
          El programa de embajadores opera actualmente solo en{" "}
          <span className="font-semibold text-amber-300">Chile</span>, por la
          jurisdicción legal y fiscal de AppWhoop SpA. La expansión a LATAM está
          en camino.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="flex justify-center"
      >
        {isAuthenticated ? (
          <Link
            to={`/perfil/${userSub}/activar-embajador`}
            className="bg-aw-green text-aw-dark font-bold px-8 py-3 rounded-xl hover:scale-95 transition-transform duration-200 text-sm"
          >
            Activar mi perfil de Embajador →
          </Link>
        ) : (
          <button
            onClick={() => openModal("register")}
            className="bg-aw-green text-aw-dark font-bold px-8 py-3 rounded-xl hover:scale-95 transition-transform duration-200 text-sm"
          >
            Activar mi perfil de Embajador →
          </button>
        )}
      </motion.div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ComoFunciona() {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { modals, openModal, closeModal } = useModalsContext();

  const [activeTab, setActiveTab] = useState("empresa");
  const [tabDir, setTabDir] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  const handleTabChange = (tabId) => {
    const prevIdx = TABS.findIndex((t) => t.id === activeTab);
    const nextIdx = TABS.findIndex((t) => t.id === tabId);
    setTabDir(nextIdx - prevIdx);
    setActiveTab(tabId);
    const el = document.getElementById("tab-content");
    const tabsBar = document.getElementById("tabs-section");
    if (el) {
      const offset = el.getBoundingClientRect().top + window.scrollY - (tabsBar?.offsetHeight ?? 56) - 16;
      window.scrollTo({ top: offset, behavior: "smooth" });
    }
  };

  const handleFaqToggle = (i) => {
    setOpenFaq((prev) => (prev === i ? null : i));
  };

  const renderTabContent = () => {
    if (activeTab === "empresa")
      return (
        <EmpresaTab
          openModal={openModal}
          isAuthenticated={isAuthenticated}
        />
      );
    if (activeTab === "asistente") return <AsistenteTab />;
    if (activeTab === "embajador")
      return (
        <EmbajadorTab
          openModal={openModal}
          isAuthenticated={isAuthenticated}
          userSub={user?.sub}
        />
      );
    return null;
  };

  return (
    <div className="min-h-screen bg-aw-bluedark text-white">
      {/* ── Auth Modals ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {modals.signIn && (
          <Modal isOpen={modals.signIn} onClose={() => closeModal("signIn")} large={"max-w-3xl"}>
            <SignIn
              onClose={() => closeModal("signIn")}
              onRegisterClick={() => {
                closeModal("signIn");
                openModal("register");
              }}
            />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {modals.register && (
          <Modal isOpen={modals.register} onClose={() => closeModal("register")} large={"max-w-3xl"}>
            <Register
              onBackToSignInClick={() => {
                closeModal("register");
                openModal("signIn");
              }}
            />
          </Modal>
        )}
      </AnimatePresence>

      {/* ── Fixed Navbar ─────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-aw-bluedark/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="shrink-0">
            <img
              src={AppWhoopLogo}
              alt="AppWhoop"
              className="h-9 w-auto object-contain"
            />
          </Link>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Link
                to="/"
                className="text-aw-green font-semibold text-sm hover:text-aw-green/80 transition-colors duration-150"
              >
                Ir al inicio →
              </Link>
            ) : (
              <>
                <button
                  onClick={() => openModal("signIn")}
                  className="text-white/80 hover:text-white text-sm font-medium transition-colors duration-150 px-3 py-1.5 rounded-lg hover:bg-white/5"
                >
                  Iniciar sesión
                </button>
                <button
                  onClick={() => openModal("register")}
                  className="bg-aw-green text-aw-dark text-sm font-bold px-4 py-1.5 rounded-lg hover:scale-95 transition-transform duration-150"
                >
                  Registrarse
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-14">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={BackgroundImg}
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-aw-bluedark/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-aw-bluedark/40 to-aw-bluedark" />
        </div>

        {/* Floating particles */}
        {[...Array(18)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${
              i % 4 === 0 ? "w-1.5 h-1.5 bg-purple-400/70" :
              i % 4 === 1 ? "w-1 h-1 bg-cyan-400/60" :
              i % 4 === 2 ? "w-1 h-1 bg-pink-400/50" :
              "w-1 h-1 bg-aw-green/60"
            }`}
            style={{
              left: `${10 + (i * 7.5) % 80}%`,
              top: `${15 + (i * 11) % 65}%`,
            }}
            animate={{
              y: [0, -(16 + (i % 3) * 10), 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + (i % 3),
              repeat: Infinity,
              delay: i * 0.35,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Central glow blob */}
        <motion.div
          className="absolute w-96 h-96 rounded-full bg-aw-green/8 blur-3xl pointer-events-none"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Purple spotlight */}
        <motion.div
          className="absolute w-72 h-72 rounded-full bg-purple-500/10 blur-3xl pointer-events-none"
          style={{ left: "10%", top: "20%" }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
        />
        {/* Cyan spotlight */}
        <motion.div
          className="absolute w-64 h-64 rounded-full bg-cyan-400/8 blur-3xl pointer-events-none"
          style={{ right: "10%", top: "30%" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        {/* Pink accent */}
        <motion.div
          className="absolute w-48 h-48 rounded-full bg-pink-500/8 blur-3xl pointer-events-none"
          style={{ right: "25%", bottom: "25%" }}
          animate={{ scale: [1, 1.4, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-4 max-w-3xl mx-auto gap-6">
          {/* Logo with glow + float */}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-full bg-aw-green/20 blur-2xl scale-150 pointer-events-none" />
            <img
              src={AppWhoopLogo}
              alt="AppWhoop"
              className="relative h-20 sm:h-28 w-auto object-contain drop-shadow-lg"
            />
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight"
          >
            Vende,{" "}
            <span className="text-aw-green">vive</span>{" "}
            y gana — todo en una sola plataforma.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl"
          >
            Los organizadores venden. Los asistentes viven experiencias. Los
            embajadores ganan comisiones. Todo conectado en AppWhoop.
          </motion.p>

          {/* Scroll hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="mt-6 group"
          >
            <motion.button
              onClick={() => document.getElementById("tabs-section")?.scrollIntoView({ behavior: "smooth" })}
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
            >
              <span className="text-white/30 text-xs tracking-widest uppercase transition-colors group-hover:text-aw-green">
                Explora
              </span>
              <FontAwesomeIcon
                icon="fa-solid fa-chevron-down"
                className="text-white/30 text-sm transition-colors group-hover:text-aw-green"
              />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── Sticky Tabs ─────────────────────────────────────────────────── */}
      <div id="tabs-section" className="sticky top-14 z-40 bg-aw-bgbluedark/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-end gap-0 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className="relative flex items-center gap-2 px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors duration-150 shrink-0 outline-none"
                style={{
                  color: activeTab === tab.id ? "var(--color-aw-green)" : "rgba(255,255,255,0.5)",
                }}
              >
                <FontAwesomeIcon icon={tab.icon} className="text-xs sm:text-sm" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">
                  {tab.id === "empresa"
                    ? "Empresa"
                    : tab.id === "asistente"
                    ? "Asistente"
                    : "Embajador"}
                </span>

                {/* Active underline */}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-aw-green rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 35 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────────── */}
      <section id="tab-content" className="max-w-6xl mx-auto px-4 py-12 sm:py-16 min-h-[500px]">
        <AnimatePresence mode="wait" custom={tabDir}>
          <motion.div
            key={activeTab}
            custom={tabDir}
            variants={tabVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </section>

      {/* ── Ecosystem Section ────────────────────────────────────────────── */}
      <section className="relative bg-aw-blueblack py-16 sm:py-20 px-4 overflow-hidden">
        {/* Techno background */}
        <div className="absolute inset-0 pointer-events-none">
          <img src={BackgroundImg} alt="" className="w-full h-full object-cover object-center opacity-10" />
          <div className="absolute inset-0 bg-aw-blueblack/80" />
        </div>
        {/* Colored beams */}
        <motion.div
          className="absolute w-80 h-80 rounded-full bg-purple-600/10 blur-3xl pointer-events-none"
          style={{ left: "-5%", top: "10%" }}
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none"
          style={{ right: "-5%", bottom: "10%" }}
          animate={{ opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 space-y-3"
          >
            <p className="text-white/40 text-xs uppercase tracking-widest font-semibold">
              Ecosistema
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              Tres roles. Un solo ecosistema.
            </h2>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              Cada actor recibe lo suyo al realizarse el corte periódico de las ganancias. Sin cálculos manuales, sin demoras.(Leer los términos y condiciones)
            </p>
          </motion.div>

          {/* Role bubbles */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 mb-10"
          >
            {/* Organizador */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-purple-400/10 border-2 border-purple-400/40 flex flex-col items-center justify-center gap-1">
                <FontAwesomeIcon
                  icon="fa-solid fa-building"
                  className="text-purple-400 text-2xl"
                />
              </div>
              <span className="text-purple-400 font-semibold text-sm">
                Organizador
              </span>
            </div>

            {/* Arrow right */}
            <div className="flex sm:flex-row flex-col items-center gap-2">
              <motion.div
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-arrow-right"
                  className="text-white/20 text-xl rotate-90 sm:rotate-0"
                />
              </motion.div>
            </div>

            {/* AppWhoop center */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-32 h-32 rounded-full bg-aw-green/10 border-2 border-aw-green/40 flex flex-col items-center justify-center gap-2 relative">
                <div className="absolute inset-0 rounded-full bg-aw-green/5 blur-xl" />
                <img
                  src={AppWhoopLogo}
                  alt="AppWhoop"
                  className="relative h-10 w-auto object-contain"
                />
              </div>
              <span className="text-aw-green font-bold text-sm">AppWhoop</span>
            </div>

            {/* Arrow right */}
            <div className="flex items-center">
              <motion.div
                animate={{ x: [0, 6, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3,
                }}
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-arrow-right"
                  className="text-white/20 text-xl rotate-90 sm:rotate-0"
                />
              </motion.div>
            </div>

            {/* Asistente */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-sky-400/10 border-2 border-sky-400/40 flex flex-col items-center justify-center gap-1">
                <FontAwesomeIcon
                  icon="fa-solid fa-ticket"
                  className="text-sky-400 text-2xl"
                />
              </div>
              <span className="text-sky-400 font-semibold text-sm">
                Asistente
              </span>
            </div>
          </motion.div>

          {/* Embajador below */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex flex-col items-center gap-3"
          >
            <div className="w-24 h-24 rounded-full bg-aw-green/10 border-2 border-aw-green/30 flex flex-col items-center justify-center gap-1">
              <FontAwesomeIcon
                icon="fa-solid fa-star"
                className="text-aw-green text-2xl"
              />
            </div>
            <span className="text-aw-green font-semibold text-sm">
              Embajador
            </span>
            <p className="text-white/40 text-xs text-center max-w-xs">
              Acumula tus comisiones por cada ticket vendido con tu enlace en el siguiente corte de ganancias.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Differentiators Section ───────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 space-y-3"
        >
          <p className="text-white/40 text-xs uppercase tracking-widest font-semibold">
            Por qué AppWhoop
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Lo que nos hace diferentes
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-aw-blueblack border border-white/5 rounded-2xl overflow-hidden"
        >
          {/* Table header */}
          <div className="grid grid-cols-3 px-6 py-4 border-b border-white/5 bg-aw-bgbluedark">
            <span className="text-white/40 text-xs uppercase tracking-widest font-semibold">
              Característica
            </span>
            <span className="text-aw-green text-xs uppercase tracking-widest font-semibold text-center">
              AppWhoop
            </span>
            <span className="text-white/40 text-xs uppercase tracking-widest font-semibold text-center">
              Otras plataformas
            </span>
          </div>

          {/* Rows */}
          {DIFFERENTIATORS.map((d, i) => (
            <div
              key={d.feature}
              className={`grid grid-cols-3 px-6 py-4 items-center ${
                i < DIFFERENTIATORS.length - 1 ? "border-b border-white/5" : ""
              } ${i % 2 === 0 ? "" : "bg-white/[0.015]"}`}
            >
              <span className="text-white/80 text-sm">{d.feature}</span>
              <div className="flex justify-center">
                <FontAwesomeIcon
                  icon="fa-solid fa-circle-check"
                  className="text-aw-green text-lg"
                />
              </div>
              <div className="flex justify-center">
                {d.other === true ? (
                  <FontAwesomeIcon
                    icon="fa-solid fa-circle-check"
                    className="text-white/30 text-lg"
                  />
                ) : d.other === "extra" ? (
                  <span className="text-white/40 text-xs bg-white/5 px-2 py-0.5 rounded-full">
                    Costo extra
                  </span>
                ) : (
                  <FontAwesomeIcon
                    icon="fa-solid fa-circle-xmark"
                    className="text-red-400/50 text-lg"
                  />
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── FAQ Section ───────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 space-y-3"
        >
          <p className="text-white/40 text-xs uppercase tracking-widest font-semibold">
            Preguntas frecuentes
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Resolvemos tus dudas
          </h2>
        </motion.div>

        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-aw-blueblack border border-white/5 rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => handleFaqToggle(i)}
                className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors duration-150"
              >
                <span className="text-white font-medium text-sm leading-snug">
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: openFaq === i ? 180 : 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="shrink-0"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-chevron-down"
                    className="text-white/40 text-sm"
                  />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div
                    key="faq-answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 pt-0">
                      <div className="h-px bg-white/5 mb-4" />
                      <p className="text-white/50 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Footer CTA ────────────────────────────────────────────────────── */}
      <section className="relative py-20 sm:py-28 px-4 bg-aw-blueblack overflow-hidden">
        {/* Background image with techno overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <img src={BackgroundImg} alt="" className="w-full h-full object-cover object-center opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-t from-aw-blueblack via-aw-blueblack/85 to-aw-blueblack/70" />
        </div>
        {/* Colored spotlights */}
        <motion.div
          className="absolute w-96 h-96 rounded-full bg-aw-green/10 blur-3xl pointer-events-none"
          style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-64 h-64 rounded-full bg-purple-500/12 blur-3xl pointer-events-none"
          style={{ left: "15%", top: "20%" }}
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
        <motion.div
          className="absolute w-56 h-56 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none"
          style={{ right: "10%", bottom: "20%" }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center space-y-8">
          {/* Floating logo */}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-aw-green/20 blur-2xl scale-150 pointer-events-none" />
              <img
                src={AppWhoopLogo}
                alt="AppWhoop"
                className="relative h-20 sm:h-24 w-auto object-contain drop-shadow-lg"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h2 className="text-3xl sm:text-4xl font-black text-white">
              ¿Listo para empezar?
            </h2>
            <p className="text-white/50 text-sm sm:text-base max-w-lg mx-auto">
              Únete a los organizadores, asistentes y embajadores que ya usan
              AppWhoop.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            {isAuthenticated ? (
              <Link
                to="/"
                className="bg-aw-green text-aw-dark font-bold px-8 py-3.5 rounded-xl hover:scale-95 transition-transform duration-150 text-sm w-full sm:w-auto text-center"
              >
                Ir a AppWhoop →
              </Link>
            ) : (
              <>
                <button
                  onClick={() => openModal("register")}
                  className="bg-aw-green text-aw-dark font-bold px-8 py-3.5 rounded-xl hover:scale-95 transition-transform duration-150 text-sm w-full sm:w-auto"
                >
                  Crear cuenta gratis
                </button>
                <button
                  onClick={() => openModal("signIn")}
                  className="bg-white/10 text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-white/15 transition-colors duration-150 text-sm w-full sm:w-auto border border-white/10"
                >
                  Ya tengo cuenta
                </button>
                <Link
                  to="/"
                  className="text-white/50 hover:text-white text-sm font-medium transition-colors duration-150 w-full sm:w-auto text-center"
                >
                  Explorar eventos
                </Link>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Mini Footer ───────────────────────────────────────────────────── */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="shrink-0">
            <img
              src={AppWhoopLogo}
              alt="AppWhoop"
              className="h-8 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-150"
            />
          </Link>
          <p className="text-white/30 text-xs text-center">
            © {new Date().getFullYear()} AppWhoop SpA. Todos los derechos
            reservados.
          </p>
          <Link
            to="/politica-de-privacidad-y-terminos-y-condiciones"
            className="text-white/30 hover:text-white/60 text-xs transition-colors duration-150"
          >
            Privacidad y Términos
          </Link>
        </div>
      </footer>
    </div>
  );
}
